import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { NbDialogService } from '@nebular/theme';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { StatsCampaignComponent } from 'src/app/features/campaigns/campaign-table/stats-campaign/stats-campaign.component';
import { StatsInsightComponent } from 'src/app/features/insights/insights-table/stats-insight/stats-insight.component';
import { EditTableColumnsComponent } from 'src/app/shared/ui/edit-table-columns/edit-table-columns.component';
import { CAMPAIGN_TYPES, UNICODE } from '../../constants/general-constants';

@Component({
  selector: 'app-ngx-mat-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ngx-mat-table.component.html',
  styleUrls: ['./ngx-mat-table.component.css'],
  standalone: false
})
export class NgxMatTableComponent implements OnInit {
  public _tableData: any[];
  private _showActions: string[] = [];

  @Input() public tableName: string;
  @Input() public dataType: string;
  @Input() public description: string;
  @Input() set tableData(value: any[]) {
    if (!value) return;
    if (value !== this._tableData) {
      this._tableData = value;
      if (this.dataSource.paginator && this.dataSource.sort) {
        this.dataSource.data = value;
      }
    }
  }
  @Input() public failedInsights: any[] = [];
  @Input() public sortActive: string;
  @Input() public sortDirection: SortDirection = 'asc';
  @Input() set showActions(value: string[]) {
    if (!value) return;
    if (value !== this._showActions) {
      this._showActions = value;
      this.displayedActions = {
        create: false,
        stats: false,
        start: false,
        stop: false,
        edit: false,
        clone: false,
        delete: false,
        favorite: false
      };
      if (this.dataType === 'robot') {
        if (this.restrictionService.qa_permission) {
          this.displayedActions.create = true;
          this.displayedActions.clone = true;
          this.displayedActions.edit = true;
          this.displayedActions.delete = true;
        }
      } else {
        if (this.restrictionService.manager_permission) {
          for (const action of value) {
            this.displayedActions[action] = true;
          }
        }
      }
    }
  }
  @Output() public clickedButton: EventEmitter<any> = new EventEmitter();

  @ViewChild(MatTable) public table: MatTable<any>;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
      if (this._tableData && this.dataSource.paginator) {
        this.dataSource.data = this._tableData;
      }
    }
  }
  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    if (paginator) {
      this.dataSource.paginator = paginator;
      if (this._tableData && this.dataSource.sort) {
        this.dataSource.data = this._tableData;
      }
    }
  }

  public defaultColumns = {
    insight: ['status', 'id', 'name', 'last_run_time', 'total_results', 'created_by'],
    campaign: ['status', 'id', 'name', 'last_run_time', 'total_results', 'created_by'],
    content: ['content_id', 'headline_text', 'min_app_version', 'created_by'],
    survey: ['survey_id', 'survey_name', 'questions'],
    robot: ['id', 'name', 'family', 'series', 'sku'],
    string: ['id', 'version', 'source', 'note'],
    file: ['id', 'location'],
    image: ['id', 'file', 'location'],
    link: ['id', 'location'],
    video: ['id', 'location'],
    robot_family_metadata: ['robot_family', 'metadata_type', 'metadata'],
    my_campaign: ['status', 'id', 'name', 'last_updated_dttm']
  };
  public displayedColumns = [];
  public displayedColumnsWithExpand = [];
  public displayedActions = {
    create: false,
    stats: false,
    start: false,
    stop: false,
    edit: false,
    clone: false,
    delete: false,
    favorite: false
  };
  public dataSource = new MatTableDataSource();
  public columnFilters = {};
  private expandedElement = null;
  public favorites;
  public campaignTypes = CAMPAIGN_TYPES;
  public selectedCampaignTypes = new Set<string>();
  public campaignTypeSearch = '';
  public executionTypes = new Set<'batch' | 'realtime'>(['batch', 'realtime']);
  private campaignTypePriority = ['Notification2', 'Contextual Message', 'eCommerce'];

  constructor(
    private dialogService: NbDialogService,
    private restrictionService: OktaRestriction
  ) {}

  public ngOnInit() {
    this.dataSource.sortingDataAccessor = this.customSortingDataAccessor;
    this.dataSource.filterPredicate = (data: any, filter: string): boolean => {
      let result = true;
      Object.keys(this.columnFilters).forEach((column) => {
        const filterValue = this.columnFilters[column].toLowerCase();
        result = result && data[column]?.toString().toLowerCase().includes(filterValue);
      });

      if (this.dataType === 'campaign' || this.dataType === 'my_campaign') {
        if (this.selectedCampaignTypes.size > 0) {
          result = result && this.selectedCampaignTypes.has(data.campaign_type?.toString());
        }
        result = result && this.executionTypes.has(data.is_real_time ? 'realtime' : 'batch');
      }

      return result;
    };
    this.favorites = JSON.parse(localStorage.getItem(this.dataType + '_favorites')) || [];
    this.displayedColumns =
      JSON.parse(localStorage.getItem(this.dataType + '_columns')) || this.defaultColumns[this.dataType];
    if (
      this.displayedColumns.includes('actions') ||
      this.displayedColumns.includes('expand') ||
      this.displayedColumns.length !== new Set(this.displayedColumns).size
    ) {
      this.displayedColumns = this.defaultColumns[this.dataType];
      localStorage.setItem(this.dataType + '_columns', JSON.stringify(this.displayedColumns));
    }
  }

  public trackById(index: number, row: any) {
    if (this.dataType === 'content') {
      return row.content_id;
    } else if (this.dataType === 'survey') {
      return row.survey_id;
    } else if (this.dataType === 'robot_family_metadata') {
      return row.robot_family;
    } else {
      return row.id;
    }
  }

  public drop(event: CdkDragDrop<string[]>) {
    if (this.dataType !== 'my_campaign') {
      if (event.previousIndex === 0) return;
      if (event.currentIndex === 0) event.currentIndex += 1;
      event.previousIndex -= 1;
      event.currentIndex -= 1;
    } else {
      if (event.previousIndex === this.displayedColumns.length) return;
      if (event.currentIndex === this.displayedColumns.length) event.currentIndex -= 1;
    }
    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
    localStorage.setItem(this.dataType + '_columns', JSON.stringify(this.displayedColumns));
  }

  public isExpanded(element) {
    return this.expandedElement === element;
  }

  public expand(element) {
    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  public editTableColumns() {
    this.dialogService
      .open(EditTableColumnsComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: {
          dataType: this.dataType,
          columns: Object.keys(this.dataSource.data[0]).sort(),
          displayedColumns: this.displayedColumns
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.displayedColumns = JSON.parse(localStorage.getItem(this.dataType + '_columns'));
          this.dataSource.filter = '';
        }
      });
  }

  public applyFilter(event: Event, column: string): void {
    const filterValue = (event.target as HTMLInputElement).value;
    if (filterValue !== '') {
      this.columnFilters[column] = filterValue.trim().toLowerCase();
    } else {
      delete this.columnFilters[column];
    }
    this.refreshTableFilter();
  }

  public customSortingDataAccessor = (item: any, property: string): string => {
    if (property === 'status') {
      const statusSortingOrder = {
        FAILED: 0,
        RUNNING: 1,
        SUCCEEDED: 2,
        ACTIVE: 3,
        INACTIVE: 4
      };
      return statusSortingOrder[item[property]];
    }
    if (typeof item[property] === 'boolean') {
      return item[property].toString();
    }
    if (typeof item[property] === 'string') {
      return item[property].toLowerCase();
    }
    if (!item[property]) {
      if (this.dataSource.sort.direction === 'desc') {
        return UNICODE.NULL + UNICODE.NULL + UNICODE.NULL;
      }

      return UNICODE.MAX_CHAR + UNICODE.MAX_CHAR + UNICODE.MAX_CHAR;
    }

    return item[property];
  };

  public clicked(event) {
    this.clickedButton.emit(event);
  }

  public openCampaignStats(campaign) {
    this.dialogService.open(StatsCampaignComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        campaign
      }
    });
  }

  public openInsightStats(insight) {
    this.dialogService.open(StatsInsightComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        insight
      }
    });
  }

  public isFavorite(row) {
    if (this.favorites.includes(row.id)) {
      return 'star';
    }
    return 'star-outline';
  }

  public toggleFavorite(row) {
    if (this.favorites.includes(row.id)) {
      this.favorites = this.favorites.filter((item) => item !== row.id);
    } else {
      this.favorites.push(row.id);
    }
    localStorage.setItem(this.dataType + '_favorites', JSON.stringify(this.favorites));
  }

  public getCreateButtonTitle() {
    if (this.dataType === 'insight') {
      return $localize`Create new insight`;
    } else if (this.dataType === 'campaign') {
      return $localize`Create new campaign`;
    } else if (this.dataType === 'content') {
      return $localize`Create new content`;
    } else if (this.dataType === 'survey') {
      return $localize`Create new survey`;
    } else if (this.dataType === 'robot') {
      return $localize`Create new robot`;
    } else if (this.dataType === 'string') {
      return $localize`Create new string`;
    } else if (this.dataType === 'file') {
      return $localize`Create new file`;
    } else if (this.dataType === 'image') {
      return $localize`Create new image`;
    } else if (this.dataType === 'link') {
      return $localize`Create new link`;
    } else if (this.dataType === 'video') {
      return $localize`Create new video`;
    } else if (this.dataType === 'robot_family_metadata') {
      return $localize`Create new robot family metadata`;
    } else {
      return $localize`Create new item`;
    }
  }

  // Campaign Filters
  private refreshTableFilter(): void {
    this.dataSource.filter = `${Date.now()}`;
  }

  public onCampaignTypeCheckedChange(value: string, checked: boolean) {
    checked ? this.selectedCampaignTypes.add(value) : this.selectedCampaignTypes.delete(value);
    this.refreshTableFilter();
  }

  public clearCampaignTypeSelections() {
    this.selectedCampaignTypes.clear();
    this.campaignTypeSearch = '';
    this.refreshTableFilter();
  }

  public get filteredCampaignTypes() {
    const searchInput = this.campaignTypeSearch.trim().toLowerCase();
    let filtered;

    if (!searchInput) {
      filtered = this.campaignTypes;
    } else {
      filtered = this.campaignTypes.filter((campaignType) => campaignType.name.toLowerCase().includes(searchInput));
    }

    return [...filtered].sort((a, b) => {
      const aIndex = this.campaignTypePriority.indexOf(a.name);
      const bIndex = this.campaignTypePriority.indexOf(b.name);
      const aWeight = aIndex === -1 ? 1000 : aIndex;
      const bWeight = bIndex === -1 ? 1000 : bIndex;

      return aWeight - bWeight || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  public onCampaignTypeSearch(value: string) {
    this.campaignTypeSearch = value ?? '';
  }

  public get selectedCampaignTypesArray(): string[] {
    return Array.from(this.selectedCampaignTypes);
  }

  public getCampaignTypeName(value: string): string {
    return this.campaignTypes.find((campaignType) => campaignType.value === value)?.name ?? value;
  }

  public removeCampaignTypeFilter(value: string) {
    this.selectedCampaignTypes.delete(value);
    this.refreshTableFilter();
  }

  public onExecutionTypeChange(type: 'batch' | 'realtime', checked: boolean) {
    checked ? this.executionTypes.add(type) : this.executionTypes.delete(type);
    this.refreshTableFilter();
  }
}
